import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, Trash2, Eye, EyeOff, Pencil, Music, Film, AlertTriangle, X, Save } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError, mediaUrl } from "@/lib/api";
import { uploadMedia, readMediaDuration, captureVideoThumbnail } from "@/lib/upload";

const LIMITS_TEXT = "MP3 até 200 MB (4 h) · MP4 até 500 MB (2 h) · capa JPG/PNG até 5 MB";
const emptyForm = () => ({ title: "", description: "", program: "", date: new Date().toISOString().slice(0, 10), published: true });

const fmtSize = (b) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(2)} GB` : `${Math.round(b / 1024 ** 2)} MB`);
const fmtDur = (s) => (s ? `${Math.floor(s / 60)}min` : "—");

const AdminEpisodes = () => {
  const [episodes, setEpisodes] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [mediaFile, setMediaFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [progress, setProgress] = useState(null); // null | {pct, label}
  const [busy, setBusy] = useState(false);
  const mediaInput = useRef(null);
  const coverInput = useRef(null);

  const load = () => adminApi.get("/admin/episodes").then((r) => setEpisodes(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const programs = [...new Set(episodes.map((e) => e.program).filter(Boolean))];

  const reset = () => {
    setForm(emptyForm());
    setMediaFile(null);
    setCoverFile(null);
    setEditingId(null);
    setProgress(null);
    if (mediaInput.current) mediaInput.current.value = "";
    if (coverInput.current) coverInput.current.value = "";
  };

  const startEdit = (ep) => {
    setEditingId(ep.id);
    setForm({ title: ep.title, description: ep.description || "", program: ep.program, date: ep.date, published: ep.published });
    setMediaFile(null);
    setCoverFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onPickMedia = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isAudio = /audio\/(mpeg|mp3)/.test(f.type) || /\.mp3$/i.test(f.name);
    const isVideo = f.type === "video/mp4" || /\.mp4$/i.test(f.name);
    if (!isAudio && !isVideo) {
      toast.error("Envie um arquivo MP3 (áudio) ou MP4 (vídeo).");
      e.target.value = "";
      return;
    }
    const max = isAudio ? 200 * 1024 ** 2 : 500 * 1024 ** 2;
    if (f.size > max) {
      toast.error(`Arquivo acima do limite de ${fmtSize(max)}.`);
      e.target.value = "";
      return;
    }
    setMediaFile(f);
    if (!form.title) setForm((s) => ({ ...s, title: f.name.replace(/\.[^.]+$/, "") }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.program.trim() || !form.date) {
      toast.error("Preencha título, programa e data.");
      return;
    }
    if (!editingId && !mediaFile) {
      toast.error("Selecione o arquivo MP3 ou MP4 da gravação.");
      return;
    }
    setBusy(true);
    try {
      let payload = { ...form, title: form.title.trim(), program: form.program.trim() };
      if (mediaFile) {
        const isAudio = /audio\/(mpeg|mp3)/.test(mediaFile.type) || /\.mp3$/i.test(mediaFile.name);
        setProgress({ pct: 0, label: "Lendo arquivo…" });
        const duration = await readMediaDuration(mediaFile);
        const maxSec = isAudio ? 4 * 3600 : 2 * 3600;
        if (duration && duration > maxSec) throw new Error(`Duração acima do limite de ${maxSec / 3600} h.`);
        setProgress({ pct: 0, label: "Enviando gravação…" });
        const media = await uploadMedia(mediaFile, { durationSeconds: duration, onProgress: (pct) => setProgress({ pct, label: "Enviando gravação…" }) });
        payload = { ...payload, kind: isAudio ? "audio" : "video", media_file_id: media.file_id, duration_seconds: duration || 0 };
        if (!coverFile && !isAudio) {
          setProgress({ pct: 100, label: "Gerando miniatura…" });
          const thumb = await captureVideoThumbnail(mediaFile);
          if (thumb) {
            const c = await uploadMedia(thumb);
            payload.cover_file_id = c.file_id;
          }
        }
      }
      if (coverFile) {
        setProgress({ pct: 100, label: "Enviando capa…" });
        const c = await uploadMedia(coverFile);
        payload.cover_file_id = c.file_id;
      }
      if (editingId) {
        const { kind, media_file_id, duration_seconds, ...upd } = payload;
        await adminApi.put(`/admin/episodes/${editingId}`, upd);
        toast.success("Episódio atualizado.");
      } else {
        await adminApi.post("/admin/episodes", payload);
        toast.success("Gravação enviada e publicada na biblioteca.");
      }
      reset();
      load();
    } catch (err) {
      const msg = err?.response ? formatApiError(err.response.data?.detail) : err?.message || "Falha no envio. Verifique sua conexão e tente novamente.";
      toast.error(msg, { duration: 8000 });
      setProgress(null);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (ep) => {
    try {
      await adminApi.post(`/admin/episodes/${ep.id}/toggle`);
      load();
    } catch {}
  };
  const remove = async (ep) => {
    if (!window.confirm(`Excluir "${ep.title}" definitivamente? O arquivo deixará de ser acessível.`)) return;
    try {
      await adminApi.delete(`/admin/episodes/${ep.id}`);
      toast.success("Episódio excluído.");
      if (editingId === ep.id) reset();
      load();
    } catch {}
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold mb-1">Biblioteca</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-[var(--fi-blue)] leading-tight">Episódios</h1>
        <p className="text-gray-500 mt-1">Gravações da emissora (áudio e vídeo) exibidas na biblioteca do app.</p>
      </div>

      <section className="rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.06)]">
        <h2 className="font-display text-xl font-extrabold uppercase text-[var(--fi-blue)] mb-3">{editingId ? "Editar episódio" : "Enviar gravação"}</h2>
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 mb-4" data-testid="episode-copyright-notice">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Envie apenas conteúdo próprio da emissora ou com autorização. Músicas com direitos autorais não devem ser enviadas.</span>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {!editingId && (
            <div className="sm:col-span-2">
              <label className="label">Arquivo da gravação (MP3 ou MP4) *</label>
              <input ref={mediaInput} data-testid="episode-media-input" type="file" accept="audio/mpeg,.mp3,video/mp4,.mp4" onChange={onPickMedia} className="input bg-white" />
              <div className="text-xs text-gray-500 mt-1">{LIMITS_TEXT}{mediaFile && ` · selecionado: ${mediaFile.name} (${fmtSize(mediaFile.size)})`}</div>
            </div>
          )}
          <div className="sm:col-span-2">
            <label className="label">Título *</label>
            <input data-testid="episode-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" maxLength={200} />
          </div>
          <div>
            <label className="label">Programa *</label>
            <input data-testid="episode-program" list="programs-list" value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} className="input" placeholder="Ex: Jornal da Manhã" />
            <datalist id="programs-list">{programs.map((p) => <option key={p} value={p} />)}</datalist>
          </div>
          <div>
            <label className="label">Data *</label>
            <input data-testid="episode-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input" />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Descrição</label>
            <textarea data-testid="episode-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input resize-none" maxLength={3000} />
          </div>
          <div>
            <label className="label">Capa (opcional{!editingId && " — vídeos geram miniatura automática"})</label>
            <input ref={coverInput} data-testid="episode-cover-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setCoverFile(e.target.files?.[0] || null)} className="input bg-white" />
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--fi-blue)] pb-2.5">
              <input data-testid="episode-published" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-[var(--fi-red)]" />
              Publicar no app
            </label>
          </div>
          {progress && (
            <div className="sm:col-span-2" data-testid="episode-upload-progress">
              <div className="flex justify-between text-xs font-bold text-[var(--fi-blue)] mb-1"><span>{progress.label}</span><span>{progress.pct}%</span></div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-[var(--fi-red)] transition-[width] duration-300" style={{ width: `${progress.pct}%` }} /></div>
            </div>
          )}
          <div className="sm:col-span-2 flex items-center gap-3 pt-1">
            <button type="submit" data-testid="episode-submit" disabled={busy} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-xs disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? <Save className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
              {editingId ? "Salvar alterações" : "Enviar e publicar"}
            </button>
            {editingId && <button type="button" onClick={reset} className="text-xs uppercase tracking-widest font-bold text-gray-500 hover:text-[var(--fi-blue)] inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> Cancelar</button>}
          </div>
        </form>
        <style>{`.input{width:100%;padding:10px 12px;border:1px solid #e5e7eb;border-radius:10px;font-size:14px;outline:none;transition:border-color .15s}.input:focus{border-color:#0D1B4B}.label{display:block;font-size:11px;text-transform:uppercase;letter-spacing:.12em;font-weight:700;color:#0D1B4B;margin-bottom:6px}`}</style>
      </section>

      <section>
        <h2 className="font-display text-xl font-extrabold uppercase text-[var(--fi-blue)] mb-3">Gravações ({episodes.length})</h2>
        <div className="space-y-2" data-testid="episode-list">
          {episodes.length === 0 && <div className="rounded-xl bg-white p-6 text-center text-gray-400">Nenhuma gravação enviada ainda.</div>}
          {episodes.map((ep) => (
            <div key={ep.id} data-testid={`episode-row-${ep.id}`} className="rounded-xl bg-white p-3 shadow-[0_6px_20px_rgba(13,27,75,0.05)] flex items-center gap-3">
              <div className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center text-gray-400">
                {ep.cover_url ? <img src={mediaUrl(ep.cover_url)} alt="" className="w-full h-full object-cover" /> : ep.kind === "video" ? <Film className="w-6 h-6" /> : <Music className="w-6 h-6" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[var(--fi-blue)] truncate">{ep.title}</div>
                <div className="text-xs text-gray-500 truncate">{ep.program} · {new Date(ep.date + "T12:00:00").toLocaleDateString("pt-BR")} · {ep.kind === "video" ? "Vídeo" : "Áudio"} · {fmtDur(ep.duration_seconds)} · {ep.size ? fmtSize(ep.size) : ""}</div>
              </div>
              <button onClick={() => toggle(ep)} data-testid={`episode-toggle-${ep.id}`} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs uppercase tracking-widest font-bold ${ep.published ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-gray-200 text-gray-600 hover:bg-gray-300"}`}>
                {ep.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} {ep.published ? "Publicado" : "Oculto"}
              </button>
              <button onClick={() => startEdit(ep)} data-testid={`episode-edit-${ep.id}`} className="w-9 h-9 rounded-lg text-[var(--fi-blue)] hover:bg-gray-100 flex items-center justify-center" aria-label="Editar"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(ep)} data-testid={`episode-delete-${ep.id}`} className="w-9 h-9 rounded-lg text-gray-400 hover:text-[var(--fi-red)] hover:bg-red-50 flex items-center justify-center" aria-label="Excluir"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AdminEpisodes;
